import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import {
  FileText, Plus, Search, Download, Copy, Trash2, Pencil, CheckCircle2,
  Send, Receipt, Wallet, AlertTriangle, ChevronLeft, ChevronRight, ArrowUpDown,
} from "@/lib/icons";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_STYLES = {
  draft: "bg-white/5 text-white/60 border-white/15",
  sent: "bg-sky-500/15 text-sky-300 border-sky-400/30",
  overdue: "bg-red-500/15 text-red-300 border-red-400/30",
  paid: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
};
const STATUS_LABELS = { draft: "Draft", sent: "Sent", overdue: "Overdue", paid: "Paid" };
const SORT_OPTIONS = [
  { value: "created_at", label: "Last updated" },
  { value: "invoice_date", label: "Invoice date" },
  { value: "due_date", label: "Due date" },
  { value: "grand_total", label: "Total" },
  { value: "invoice_number", label: "Invoice #" },
];
const LIMIT = 10;

const fmtMoney = (amount, currency = "INR") => {
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 0 }).format(amount || 0);
  } catch {
    return `${currency} ${Number(amount || 0).toLocaleString("en-IN")}`;
  }
};
const fmtDate = (value) => {
  if (!value) return "-";
  try {
    return new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "short", year: "numeric" }).format(new Date(value));
  } catch {
    return "-";
  }
};

export default function InvoiceDashboard() {
  const [summary, setSummary] = useState(null);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("created_at");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [busyId, setBusyId] = useState("");

  const loadSummary = useCallback(() => {
    api.get("/invoices/summary").then((r) => setSummary(r.data)).catch(() => setSummary(null));
  }, []);

  const loadInvoices = useCallback(() => {
    setLoading(true);
    api.get("/invoices", { params: { q, status, sort, order, page, limit: LIMIT } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatError(e)))
      .finally(() => setLoading(false));
  }, [q, status, sort, order, page]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  const refresh = () => { loadSummary(); loadInvoices(); };

  const download = async (inv) => {
    setBusyId(inv.id);
    try {
      const res = await api.post(`/invoices/${inv.id}/download`, null, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${inv.invoice_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusyId("");
    }
  };

  const duplicate = async (inv) => {
    setBusyId(inv.id);
    try {
      const { data: created } = await api.post(`/invoices/${inv.id}/duplicate`);
      toast.success(`Duplicated as ${created.invoice_number}`);
      refresh();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusyId(""); }
  };

  const markPaid = async (inv) => {
    setBusyId(inv.id);
    try {
      await api.post(`/invoices/${inv.id}/mark-paid`, {});
      toast.success("Marked as paid");
      refresh();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusyId(""); }
  };

  const remove = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_number}? This can't be undone.`)) return;
    setBusyId(inv.id);
    try {
      await api.delete(`/invoices/${inv.id}`);
      toast.success("Invoice deleted");
      refresh();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusyId(""); }
  };

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / LIMIT));

  return (
    <div className="glass rounded-3xl p-6 md:p-8" data-testid="invoice-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="overline text-fuchsia-300">Invoicing</div>
          <div className="mt-2 font-display text-3xl text-white">Your invoices</div>
        </div>
        <Link to="/account/invoices/new" data-testid="invoice-create-btn"
          className="btn-glow inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white">
          <Plus size={14} /> Create Invoice
        </Link>
      </div>

      {/* STATS */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard icon={FileText} label="Total invoices" value={summary?.total} />
        <StatCard icon={CheckCircle2} label="Paid" value={summary?.paid} tone="emerald" />
        <StatCard icon={Send} label="Pending" value={(summary?.sent || 0) + (summary?.overdue || 0)} tone="sky" />
        <StatCard icon={Pencil} label="Draft" value={summary?.draft} />
        <StatCard icon={Wallet} label="Total revenue" value={summary ? fmtMoney(summary.total_revenue) : "-"} tone="emerald" wide />
        <StatCard icon={AlertTriangle} label="Outstanding" value={summary ? fmtMoney(summary.outstanding_amount) : "-"} tone="amber" wide />
      </div>

      {/* FILTERS */}
      <div className="mt-6 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input
            data-testid="invoice-search"
            placeholder="Search invoice # or customer…"
            className="pl-9 h-10 bg-white/5 border-white/10 text-white placeholder:text-white/40"
            value={q}
            onChange={(e) => { setPage(1); setQ(e.target.value); }}
          />
        </div>
        <Select value={status} onValueChange={(v) => { setPage(1); setStatus(v); }}>
          <SelectTrigger className="w-36 h-10 bg-white/5 border-white/10 text-white" data-testid="invoice-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-40 h-10 bg-white/5 border-white/10 text-white" data-testid="invoice-sort"><SelectValue /></SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <button onClick={() => setOrder((o) => o === "asc" ? "desc" : "asc")}
          data-testid="invoice-sort-order" title="Toggle sort direction"
          className="btn-ghost inline-flex items-center justify-center h-10 w-10 rounded-full text-white/70">
          <ArrowUpDown size={14} />
        </button>
      </div>

      {/* TABLE */}
      <div className="mt-5 rounded-2xl border border-white/10 overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="text-white/50 bg-white/5">
              <th className="text-left px-4 py-3 font-semibold">Invoice #</th>
              <th className="text-left px-4 py-3 font-semibold">Customer</th>
              <th className="text-left px-4 py-3 font-semibold">Date</th>
              <th className="text-left px-4 py-3 font-semibold">Due date</th>
              <th className="text-right px-4 py-3 font-semibold">Total</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              <th className="text-right px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              [...Array(4)].map((_, i) => (
                <tr key={i} className="border-t border-white/5">
                  <td colSpan={7} className="px-4 py-4">
                    <div className="h-4 rounded bg-white/5 animate-pulse" style={{ width: `${70 - i * 8}%` }} />
                  </td>
                </tr>
              ))
            )}
            {!loading && data.items.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-16">
                  <div className="flex flex-col items-center text-center gap-3" data-testid="invoice-empty-state">
                    <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-white/5 border border-white/10">
                      <Receipt size={22} className="text-white/40" />
                    </span>
                    <div className="text-white/70 font-medium">No invoices yet</div>
                    <p className="text-white/45 text-xs max-w-xs">Create your first invoice to start billing clients directly from PRONET.</p>
                    <Link to="/account/invoices/new"
                      className="btn-glow inline-flex items-center gap-2 rounded-full px-5 py-2 text-xs font-semibold text-white mt-1">
                      <Plus size={12} /> Create Invoice
                    </Link>
                  </div>
                </td>
              </tr>
            )}
            {!loading && data.items.map((inv) => (
              <tr key={inv.id} className="border-t border-white/5 hover:bg-white/5 transition-colors" data-testid={`invoice-row-${inv.invoice_number}`}>
                <td className="px-4 py-3 font-mono text-white/85">{inv.invoice_number}</td>
                <td className="px-4 py-3 text-white/80 max-w-[160px] truncate">{inv.customer?.name || "-"}</td>
                <td className="px-4 py-3 text-white/60">{fmtDate(inv.invoice_date)}</td>
                <td className="px-4 py-3 text-white/60">{fmtDate(inv.due_date)}</td>
                <td className="px-4 py-3 text-right text-white/85 tabular-nums">{fmtMoney(inv.totals?.grand_total, inv.currency)}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold border ${STATUS_STYLES[inv.effective_status] || STATUS_STYLES.draft}`}>
                    {STATUS_LABELS[inv.effective_status] || "Draft"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    {inv.effective_status !== "paid" && (
                      <IconBtn title="Mark as paid" onClick={() => markPaid(inv)} disabled={busyId === inv.id} tone="text-emerald-300 hover:bg-emerald-500/10">
                        <CheckCircle2 size={14} />
                      </IconBtn>
                    )}
                    <IconBtn title="Download PDF" onClick={() => download(inv)} disabled={busyId === inv.id} tone="text-white/60 hover:bg-white/10">
                      <Download size={14} />
                    </IconBtn>
                    <IconBtn title="Duplicate" onClick={() => duplicate(inv)} disabled={busyId === inv.id} tone="text-white/60 hover:bg-white/10">
                      <Copy size={14} />
                    </IconBtn>
                    <Link to={`/account/invoices/${inv.id}`} title="Edit"
                      className="p-2 rounded-lg text-white/60 hover:bg-white/10 transition-colors">
                      <Pencil size={14} />
                    </Link>
                    <IconBtn title="Delete" onClick={() => remove(inv)} disabled={busyId === inv.id} tone="text-red-300 hover:bg-red-500/10">
                      <Trash2 size={14} />
                    </IconBtn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      {data.total > LIMIT && (
        <div className="mt-4 flex items-center justify-between text-sm text-white/50">
          <div>Page {page} of {totalPages} · {data.total} total</div>
          <div className="flex gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
              className="btn-ghost inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs disabled:opacity-40">
              <ChevronLeft size={13} /> Prev
            </button>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
              className="btn-ghost inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs disabled:opacity-40">
              Next <ChevronRight size={13} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone, wide }) {
  const toneCls = {
    emerald: "border-emerald-400/25 bg-emerald-500/10 text-emerald-300",
    sky: "border-sky-400/25 bg-sky-500/10 text-sky-300",
    amber: "border-amber-400/25 bg-amber-500/10 text-amber-300",
  }[tone] || "border-[#9D00FF]/25 bg-[#9D00FF]/10 text-[#c66bff]";
  return (
    <div className={`glass rounded-2xl p-4 ${wide ? "col-span-2 sm:col-span-1" : ""}`}>
      <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg border ${toneCls}`}>
        <Icon size={14} />
      </span>
      <div className="mt-3 font-display text-xl text-white tabular-nums truncate">{value ?? "-"}</div>
      <div className="mt-0.5 text-[11px] font-medium text-white/50">{label}</div>
    </div>
  );
}

function IconBtn({ children, title, tone, ...rest }) {
  return (
    <button title={title} className={`p-2 rounded-lg transition-colors disabled:opacity-40 ${tone}`} {...rest}>
      {children}
    </button>
  );
}
